/**
 * Is the chat header's green call chip ("0:42") really on screen? On phones the minimized call shows as the
 * chip in its conversation's header instead of the floating pill, but that header slides away under a sheet
 * and can be covered by any full-screen layer (the live browser, a large sheet, a previewer). Rather than
 * every layer reporting itself, the header registers its chip and this checks, while it matters, whether the
 * chip is the topmost thing at its own centre. When it isn't, the pill comes back so the call stays reachable.
 */
import { computed, onBeforeUnmount, ref, shallowReactive, watch, type ComputedRef, type Ref } from 'vue'

const chips = shallowReactive(new Set<HTMLElement>())

/** Elements to look through when hit-testing (the call's own pill and screen). */
export const CALL_OVERLAY_ATTR = 'data-call-overlay'

export function isChipUncovered(el: HTMLElement): boolean {
    if (!el.isConnected || typeof document === 'undefined') return false
    const r = el.getBoundingClientRect()
    if (r.width < 4 || r.height < 4) return false
    const x = r.left + r.width / 2
    const y = r.top + r.height / 2
    if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false
    const check = (el as HTMLElement & { checkVisibility?: (o?: Record<string, boolean>) => boolean }).checkVisibility
    if (check && !check.call(el, { opacityProperty: true, visibilityProperty: true })) return false
    const stack = typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(x, y) : [document.elementFromPoint(x, y)]
    for (const hit of stack) {
        if (!hit) continue
        if (hit.closest(`[${CALL_OVERLAY_ATTR}]`)) continue
        return hit === el || el.contains(hit)
    }
    return false
}

function measure(): boolean {
    for (const el of chips) if (isChipUncovered(el)) return true
    return false
}

/**
 * While `active` (the call is minimized and a chip may stand in for the pill), re-check a few times a
 * second. It's measured as it's read too, so the first frame after minimizing is already right (no pill
 * flashing in and out). One check is a couple of hit tests. (One consumer: VoiceCallHost.)
 */
export function useCallChipVisible(active: () => boolean): ComputedRef<boolean> {
    const tick = ref(0)
    let timer: ReturnType<typeof setInterval> | null = null
    const halt = () => {
        if (timer) clearInterval(timer)
        timer = null
    }
    const on = computed(() => active() && chips.size > 0)
    watch(on, (yes) => {
        halt()
        if (yes) timer = setInterval(() => { tick.value++ }, 300)
    }, { immediate: true })
    onBeforeUnmount(halt)
    return computed(() => {
        void tick.value
        return on.value && measure()
    })
}

/** The header's chip element, registered while it's rendered. */
export function useCallChip(el: Ref<HTMLElement | null>): void {
    let current: HTMLElement | null = null
    watch(el, (next) => {
        if (current) chips.delete(current)
        current = next
        if (next) chips.add(next)
    }, { immediate: true, flush: 'post' })
    onBeforeUnmount(() => {
        if (current) chips.delete(current)
        current = null
    })
}
